//============================================================================
// TIMETRACKER PRO - SUPABASE HIRING HOOKS
// ============================================================================
// Manages hiring_candidates and hiring_interviews tables.
// Follows existing hook patterns from useSupabase.js:
//   - requireSession() for RLS compliance
//   - formatSupabaseError() for user-friendly errors
//   - snake_case (DB) ↔ camelCase (App) mapping
//   - { success, data?, error? } return signatures
//
// v2: Added phone fields, hired_user_id link, hireCandidate() action.
// v3: Fixed interview_taken_by/suitable_project_ids to UUID/string (not bigint).
//     Added address fields to candidates for alignment with user_profiles.
// v4: Added degrees jsonb to candidates. hireCandidate() now populates
//     employee profile, address, employers, education via Supabase direct
//     writes when Edge Function is unavailable.
// v5: Post-hire population refactored to use standalone helpers from
//     useTeamMemberProfile.js (replaceEducationForUser, replaceEmployersForUser,
//     upsertPrimaryHomeAddressForUser). Supports replace semantics (delete+insert)
//     and normalizes both string and object pastEmployment entries.
// v6: On hire, automatically writes a "Hiring Interview" note into
//     public.user_notes. Note insertion is done by HiringPage (not this hook).
//     Note insert is non-blocking — hire succeeds even if note insert fails.
// ============================================================================
//
// ── SQL MIGRATION NOTES (run manually, not executed by app) ─────────────
//
// -- Part A: Fix interview column types (only if currently bigint/int)
// ALTER TABLE hiring_interviews
//   ALTER COLUMN interview_taken_by TYPE uuid USING interview_taken_by::uuid;
//
// ALTER TABLE hiring_interviews
//   ALTER COLUMN suitable_project_ids TYPE uuid[] USING suitable_project_ids::uuid[];
//
// -- (If columns are already text/text[], keep as-is — no cast needed.)
//
// -- Part C: Add address columns to hiring_candidates
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS address_line1 text;
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS address_line2 text;
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS address_city text;
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS address_state text;
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS address_postal_code text;
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS address_country text;
//
// -- Part D: Add degrees column to hiring_candidates (v4)
// ALTER TABLE hiring_candidates ADD COLUMN IF NOT EXISTS degrees jsonb DEFAULT '[]'::jsonb;
//
// ────────────────────────────────────────────────────────────────────────
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';       // same path as useSupabase.js
import { formatSupabaseError } from './useSupabase'; // co-located in src/hooks/
import {
  replaceEducationForUser,
  replaceEmployersForUser,
  upsertPrimaryHomeAddressForUser,
  addUserNote,
  composeHiringInterviewNote,
} from './useTeamMemberProfile'; // Post-hire profile population helpers (v5+v6)

// ============================================================================
// UUID VALIDATION (interview_taken_by is UUID in Supabase)
// ============================================================================
const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

// ============================================================================
// SESSION GUARD (mirrors useSupabase.js)
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    console.error('[requireSession] Session error:', sessionError);
    throw new Error('Authentication error: ' + sessionError.message);
  }
  if (!session || !session.user) {
    console.error('[requireSession] No active Supabase session');
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions.'
    );
  }
  return session.user.id;
};

// ============================================================================
// CONSTANTS
// ============================================================================
// DEPRECATED: HIRING_CATEGORIES is a fallback default only.
// The authoritative list is now stored in app_settings (key 'candidateRoles')
// and managed via Settings > Hiring tab. See useSettings.js candidateRoles.
// This export is kept for backward compatibility with any code that still
// imports it directly.
export const HIRING_CATEGORIES = [
  'Architecture',
  'Interior Designer',
  'Model Maker',
  'Graphic Designer',
  'HR',
  'Admin',
  'PR'
];

export const SOFTWARE_PROFICIENCY_KEYS = [
  { key: 'adobe', label: 'Adobe Suite' },
  { key: 'autocad', label: 'AutoCAD' },
  { key: 'revit', label: 'Revit' },
  { key: 'rhino', label: 'Rhino' },
  { key: 'sketchup', label: 'SketchUp' },
  { key: 'enscape', label: 'Enscape' },
  { key: 'lumion', label: 'Lumion' },
  { key: 'ai_image_generating', label: 'AI Image Gen' },
  { key: 'coding', label: 'Coding' }
];

// ============================================================================
// FIELD MAPPING: DB (snake_case) ↔ App (camelCase)
// ============================================================================
const mapCandidateFromDB = (c) => ({
  id: c.id,
  createdAt: c.created_at,
  createdBy: c.created_by,
  updatedAt: c.updated_at,
  updatedBy: c.updated_by,
  isArchived: c.is_archived ?? false,
  archivedAt: c.archived_at,
  archivedBy: c.archived_by,
  archiveReason: c.archive_reason || null,
  category: c.category,
  candidateName: c.candidate_name,
  jobRole: c.job_role || '',
  locationCity: c.location_city || '',
  employedStatus: c.employed_status || 'not_employed',
  pastEmployment: c.past_employment || [],
  yearsExperience: c.years_experience,
  possibleJoiningDate: c.possible_joining_date || '',
  portfolioUrl: c.portfolio_url || '',
  notes: c.notes || '',
  // Email (v5)
  email: c.email || '',
  // Phone fields (v2)
  phoneMobile: c.phone_mobile || '',
  phoneWhatsapp: c.phone_whatsapp || '',
  phoneCustomLabel: c.phone_custom_label || '',
  phoneCustomValue: c.phone_custom_value || '',
  // Address fields (v3 — aligned with user_profiles / user_addresses)
  addressLine1: c.address_line1 || '',
  addressLine2: c.address_line2 || '',
  addressCity: c.address_city || '',
  addressState: c.address_state || '',
  addressPostalCode: c.address_postal_code || '',
  addressCountry: c.address_country || '',
  // Degrees / Education (v4 — jsonb array of {type, institution, graduationDate, duration, notes})
  degrees: c.degrees || [],
  // Hire link (v2)
  hiredUserId: c.hired_user_id ?? null,
  hiredAt: c.hired_at || null,
  hiredBy: c.hired_by || null,
  // Pending admin action (offer/hire workflow)
  pendingAdminAction: c.pending_admin_action || null,
  pendingAdminActionAt: c.pending_admin_action_at || null,
  pendingAdminActionBy: c.pending_admin_action_by || null,
});

const mapCandidateToDB = (data) => {
  const db = {};
  if (data.category !== undefined) db.category = data.category;
  if (data.candidateName !== undefined) db.candidate_name = data.candidateName;
  if (data.jobRole !== undefined) db.job_role = data.jobRole || null;
  if (data.locationCity !== undefined) db.location_city = data.locationCity || null;
  if (data.employedStatus !== undefined) db.employed_status = data.employedStatus || 'not_employed';
  if (data.pastEmployment !== undefined) db.past_employment = data.pastEmployment || [];
  if (data.yearsExperience !== undefined) db.years_experience = data.yearsExperience;
  if (data.possibleJoiningDate !== undefined) db.possible_joining_date = data.possibleJoiningDate || null;
  if (data.portfolioUrl !== undefined) db.portfolio_url = data.portfolioUrl || null;
  if (data.notes !== undefined) db.notes = data.notes || null;
  if (data.isArchived !== undefined) db.is_archived = data.isArchived;
  if (data.archivedAt !== undefined) db.archived_at = data.archivedAt;
  if (data.archivedBy !== undefined) db.archived_by = data.archivedBy;
  if (data.archiveReason !== undefined) db.archive_reason = data.archiveReason || null;
  // Email (v5)
  if (data.email !== undefined) db.email = data.email || null;
  // Phone fields (v2)
  if (data.phoneMobile !== undefined) db.phone_mobile = data.phoneMobile || null;
  if (data.phoneWhatsapp !== undefined) db.phone_whatsapp = data.phoneWhatsapp || null;
  if (data.phoneCustomLabel !== undefined) db.phone_custom_label = data.phoneCustomLabel || null;
  if (data.phoneCustomValue !== undefined) db.phone_custom_value = data.phoneCustomValue || null;
  // Address fields (v3)
  if (data.addressLine1 !== undefined) db.address_line1 = data.addressLine1 || null;
  if (data.addressLine2 !== undefined) db.address_line2 = data.addressLine2 || null;
  if (data.addressCity !== undefined) db.address_city = data.addressCity || null;
  if (data.addressState !== undefined) db.address_state = data.addressState || null;
  if (data.addressPostalCode !== undefined) db.address_postal_code = data.addressPostalCode || null;
  if (data.addressCountry !== undefined) db.address_country = data.addressCountry || null;
  // Degrees (v4)
  if (data.degrees !== undefined) db.degrees = data.degrees || [];
  // Hire link (v2) — typically set by hireCandidate, not user edits
  if (data.hiredUserId !== undefined) db.hired_user_id = data.hiredUserId;
  if (data.hiredAt !== undefined) db.hired_at = data.hiredAt;
  if (data.hiredBy !== undefined) db.hired_by = data.hiredBy;
  // Pending admin action (offer/hire workflow)
  if (data.pendingAdminAction !== undefined) db.pending_admin_action = data.pendingAdminAction;
  if (data.pendingAdminActionAt !== undefined) db.pending_admin_action_at = data.pendingAdminActionAt;
  if (data.pendingAdminActionBy !== undefined) db.pending_admin_action_by = data.pendingAdminActionBy;
  return db;
};

const mapInterviewFromDB = (i) => ({
  id: i.id,
  createdAt: i.created_at,
  createdBy: i.created_by,
  updatedAt: i.updated_at,
  updatedBy: i.updated_by,
  candidateId: i.candidate_id,
  interviewIndex: i.interview_index,
  interviewDate: i.interview_date || '',
  overallComments: i.overall_comments || '',
  softwareProficiency: i.software_proficiency || {},
  salaryExpectationsMonthly: i.salary_expectations_monthly,
  currentSalaryMonthly: i.current_salary_monthly,
  futurePlans: i.future_plans || '',
  // interview_taken_by is UUID (references auth.users or public.users PK)
  interviewTakenBy: i.interview_taken_by || null,
  // suitable_project_ids is text[]/uuid[] — preserve as strings
  suitableProjectIds: (i.suitable_project_ids || []).filter(Boolean),
  finalVerdictNotes: i.final_verdict_notes || '',
  recommended: i.recommended === true ? true : i.recommended === false ? false : null
});

const mapInterviewToDB = (data) => {
  const db = {};
  if (data.candidateId !== undefined) db.candidate_id = data.candidateId;
  if (data.interviewIndex !== undefined) db.interview_index = data.interviewIndex;
  if (data.interviewDate !== undefined) db.interview_date = data.interviewDate || null;
  if (data.overallComments !== undefined) db.overall_comments = data.overallComments || null;
  if (data.softwareProficiency !== undefined) db.software_proficiency = data.softwareProficiency || {};
  if (data.salaryExpectationsMonthly !== undefined) db.salary_expectations_monthly = data.salaryExpectationsMonthly;
  if (data.currentSalaryMonthly !== undefined) db.current_salary_monthly = data.currentSalaryMonthly;
  if (data.futurePlans !== undefined) db.future_plans = data.futurePlans || null;
  // interview_taken_by is UUID string (or null) — validate before sending
  if (data.interviewTakenBy !== undefined) {
    db.interview_taken_by = isUuid(data.interviewTakenBy) ? data.interviewTakenBy : null;
  }
  // suitable_project_ids is JSONB — must be a valid JSON array, not text[]
  if (data.suitableProjectIds !== undefined) {
    db.suitable_project_ids = Array.isArray(data.suitableProjectIds) ? data.suitableProjectIds.filter(Boolean) : [];
  }
  if (data.finalVerdictNotes !== undefined) db.final_verdict_notes = data.finalVerdictNotes || null;
  if (data.recommended !== undefined) db.recommended = data.recommended === true ? true : data.recommended === null ? null : false;
  return db;
};

// ============================================================================
// MAIN HOOK: useSupabaseHiring
// ============================================================================
export const useSupabaseHiring = ({ enabled = true } = {}) => {
  const [candidates, setCandidates] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(enabled);

  // ------------------------------------------------------------------
  // FETCH ALL CANDIDATES
  // ------------------------------------------------------------------
  const fetchCandidates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('hiring_candidates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCandidates((data || []).map(mapCandidateFromDB));
    } catch (err) {
      console.error('[Hiring] Error fetching candidates:', err);
    }
  }, []);

  // ------------------------------------------------------------------
  // FETCH ALL INTERVIEWS
  // ------------------------------------------------------------------
  const fetchInterviews = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('hiring_interviews')
        .select('*')
        .order('interview_index', { ascending: true });

      if (error) throw error;
      setInterviews((data || []).map(mapInterviewFromDB));
    } catch (err) {
      console.error('[Hiring] Error fetching interviews:', err);
    }
  }, []);

  // ------------------------------------------------------------------
  // INITIAL FETCH
  // ------------------------------------------------------------------
  const refetch = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchCandidates(), fetchInterviews()]);
    setLoading(false);
  }, [fetchCandidates, fetchInterviews]);

  useEffect(() => { if (enabled) refetch(); }, [enabled, refetch]);

  // ------------------------------------------------------------------
  // LIST CANDIDATES (filtered helper — runs on local state)
  // Supports viewMode: 'current' | 'archived' | 'hired'
  // Falls back to legacy `archived` boolean if viewMode not supplied.
  // ------------------------------------------------------------------
  const listCandidates = useCallback(({ archived, category, viewMode: vm } = {}) => {
    let result = candidates;
    if (vm === 'current') {
      result = result.filter(c => !c.isArchived);
    } else if (vm === 'archived') {
      // Archived but NOT hired
      result = result.filter(c => c.isArchived && c.archiveReason !== 'Hired' && !c.hiredUserId);
    } else if (vm === 'hired') {
      // Archived via Hire or has a linked user id
      result = result.filter(c => c.isArchived && (c.archiveReason === 'Hired' || !!c.hiredUserId));
    } else if (archived !== undefined) {
      // Legacy: simple boolean filter
      result = result.filter(c => c.isArchived === archived);
    }
    if (category) {
      result = result.filter(c => c.category === category);
    }
    return result;
  }, [candidates]);

  // ------------------------------------------------------------------
  // CREATE CANDIDATE
  // ------------------------------------------------------------------
  const createCandidate = async (payload) => {
    try {
      const authUid = await requireSession();
      const dbData = {
        ...mapCandidateToDB(payload),
        created_by: authUid,
        updated_by: authUid
      };

      const { data, error } = await supabase
        .from('hiring_candidates')
        .insert([dbData])
        .select()
        .single();

      if (error) throw error;
      await fetchCandidates();
      return { success: true, data: mapCandidateFromDB(data) };
    } catch (err) {
      console.error('[Hiring] Error creating candidate:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'candidate' }) };
    }
  };

  // ------------------------------------------------------------------
  // UPDATE CANDIDATE
  // ------------------------------------------------------------------
  const updateCandidate = async (id, updates) => {
    try {
      const authUid = await requireSession();
      if (!id) throw new Error('updateCandidate called without a valid id');

      const mapped = mapCandidateToDB(updates);

      // When archiving, set archived_by to the authenticated UUID
      // (the UI cannot know this — currentUser.id is a numeric app ID)
      if (updates.isArchived === true && !mapped.archived_by) {
        mapped.archived_by = authUid;
      }

      const dbUpdates = {
        ...mapped,
        updated_by: authUid,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('hiring_candidates')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      await fetchCandidates();
      return { success: true, data: mapCandidateFromDB(data) };
    } catch (err) {
      console.error('[Hiring] Error updating candidate:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'candidate' }) };
    }
  };

  // ------------------------------------------------------------------
  // DELETE CANDIDATE (admin only — enforced at RLS + UI)
  // ------------------------------------------------------------------
  const deleteCandidate = async (id) => {
    try {
      await requireSession();
      if (!id) throw new Error('deleteCandidate called without a valid id');

      const { error } = await supabase
        .from('hiring_candidates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await Promise.all([fetchCandidates(), fetchInterviews()]);
      return { success: true };
    } catch (err) {
      console.error('[Hiring] Error deleting candidate:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'candidate' }) };
    }
  };

  // ------------------------------------------------------------------
  // CREATE INTERVIEW
  // ------------------------------------------------------------------
  const createInterview = async (candidateId, payload) => {
    try {
      const authUid = await requireSession();
      const dbData = {
        ...mapInterviewToDB(payload),
        candidate_id: candidateId,
        created_by: authUid,
        updated_by: authUid
      };

      const { data, error } = await supabase
        .from('hiring_interviews')
        .insert([dbData])
        .select()
        .single();

      if (error) throw error;
      await fetchInterviews();
      return { success: true, data: mapInterviewFromDB(data) };
    } catch (err) {
      console.error('[Hiring] Error creating interview:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'interview' }) };
    }
  };

  // ------------------------------------------------------------------
  // UPDATE INTERVIEW
  // ------------------------------------------------------------------
  const updateInterview = async (interviewId, updates) => {
    try {
      const authUid = await requireSession();
      if (!interviewId) throw new Error('updateInterview called without a valid id');

      const dbUpdates = {
        ...mapInterviewToDB(updates),
        updated_by: authUid,
        updated_at: new Date().toISOString()
      };

      console.log('[Hiring] updateInterview dbUpdates:', dbUpdates);
      if ('interview_taken_by' in dbUpdates && dbUpdates.interview_taken_by && !isUuid(dbUpdates.interview_taken_by)) {
        throw new Error(`Invalid interviewer UUID: ${dbUpdates.interview_taken_by}`);
      }

      const { data, error } = await supabase
        .from('hiring_interviews')
        .update(dbUpdates)
        .eq('id', interviewId)
        .select()
        .single();

      if (error) throw error;
      await fetchInterviews();
      return { success: true, data: mapInterviewFromDB(data) };
    } catch (err) {
      console.error('[Hiring] Error updating interview:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'interview' }) };
    }
  };

  // ------------------------------------------------------------------
  // DELETE INTERVIEW
  // ------------------------------------------------------------------
  const deleteInterview = async (interviewId) => {
    try {
      await requireSession();
      if (!interviewId) throw new Error('deleteInterview called without a valid id');

      const { error } = await supabase
        .from('hiring_interviews')
        .delete()
        .eq('id', interviewId);

      if (error) throw error;
      await fetchInterviews();
      return { success: true };
    } catch (err) {
      console.error('[Hiring] Error deleting interview:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'interview' }) };
    }
  };

  // ------------------------------------------------------------------
  // GET INTERVIEWS FOR A CANDIDATE
  // ------------------------------------------------------------------
  const getInterviewsForCandidate = useCallback((candidateId) => {
    return interviews
      .filter(i => i.candidateId === candidateId)
      .sort((a, b) => a.interviewIndex - b.interviewIndex);
  }, [interviews]);

  // ------------------------------------------------------------------
  // HIRE CANDIDATE (v2 — admin only; v6 — returns latestInterview for notes)
  // ------------------------------------------------------------------
  // Calls the admin-hire-candidate Edge Function which:
  //   1. Creates a row in public.users
  //   2. Upserts user_profiles with hiring snapshot
  //   3. Updates hiring_candidates with hired_user_id + archive
  // Then client-side:
  //   4. Populates address, employers, education
  // Returns { success, data?: { userId, latestInterview, candidateName } }
  //   ↳ latestInterview + candidateName are passed back so HiringPage
  //     can compose and insert the hiring note (user_notes) in its own
  //     try/catch, enabling a differentiated toast on note failure.
  // ------------------------------------------------------------------
  const hireCandidate = async (candidateId, hirePayload, activeUsers = []) => {
    try {
      await requireSession();
      if (!candidateId) throw new Error('hireCandidate called without a candidateId');

      // Ensure the edge function receives a valid JWT.
      // getSession() only returns the cached token which may have expired.
      // Wrap in try/catch because refreshSession() can throw (e.g. AuthSessionMissingError)
      // which would otherwise propagate to the outer catch and be mis-reported as
      // "Session expired" via formatSupabaseError.
      // Additionally, a failed refresh can clear the session entirely, causing the
      // Functions client to send an empty bearer token — so we verify the session
      // still exists after the attempt.
      try {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.warn('[Hiring] Session refresh warning:', refreshError.message);
          // Non-fatal: the existing token might still be valid
        }
      } catch (refreshErr) {
        console.warn('[Hiring] Session refresh exception (non-fatal):', refreshErr);
      }

      // Verify session wasn't cleared by a failed refresh attempt
      const { data: { session: postRefreshSession } } = await supabase.auth.getSession();
      if (!postRefreshSession) {
        throw new Error('Your login session is no longer active. Please sign in again and retry.');
      }

      // Gather candidate + latest interview for the Edge Function
      const candidate = candidates.find(c => c.id === candidateId);
      if (!candidate) throw new Error('Candidate not found in local state');
      if (candidate.hiredUserId) throw new Error('Candidate has already been hired');

      const candInterviews = interviews
        .filter(i => i.candidateId === candidateId)
        .sort((a, b) => b.interviewIndex - a.interviewIndex); // newest first
      const latestInterview = candInterviews[0] || null;

      // Build Edge Function payload
      const body = {
        candidateId,
        candidate: {
          candidateName: candidate.candidateName,
          category: candidate.category,
          locationCity: candidate.locationCity,
          pastEmployment: candidate.pastEmployment,
          yearsExperience: candidate.yearsExperience,
          possibleJoiningDate: candidate.possibleJoiningDate,
          portfolioUrl: candidate.portfolioUrl,
          notes: candidate.notes,
          phoneMobile: candidate.phoneMobile,
          phoneWhatsapp: candidate.phoneWhatsapp,
          phoneCustomLabel: candidate.phoneCustomLabel,
          phoneCustomValue: candidate.phoneCustomValue,
          addressLine1: candidate.addressLine1,
          addressLine2: candidate.addressLine2,
          addressCity: candidate.addressCity,
          addressState: candidate.addressState,
          addressPostalCode: candidate.addressPostalCode,
          addressCountry: candidate.addressCountry,
          degrees: candidate.degrees,
        },
        latestInterview: latestInterview ? {
          softwareProficiency: latestInterview.softwareProficiency,
          salaryExpectationsMonthly: latestInterview.salaryExpectationsMonthly,
          currentSalaryMonthly: latestInterview.currentSalaryMonthly,
          futurePlans: latestInterview.futurePlans,
          overallComments: latestInterview.overallComments,
          finalVerdictNotes: latestInterview.finalVerdictNotes,
        } : null,
        // Onboarding fields from Hire modal
        workEmail: hirePayload.workEmail,
        teamId: hirePayload.teamId,
        jobRole: hirePayload.jobRole,
        jobLevel: hirePayload.jobLevel,
        joinDate: hirePayload.joinDate,
        role: hirePayload.role || 'user',
        contractedHours: hirePayload.contractedHours ?? null,
        officeLocation: hirePayload.officeLocation || 'NL',
        // NL salary matrix placement (CAO schaal/trede)
        nlSchaal: hirePayload.nlSchaal || null,
        nlTrede: hirePayload.nlTrede ?? null,
      };

      const { data: fnData, error: fnError, response: fnResponse } = await supabase.functions.invoke(
        'admin-hire-candidate',
        { body }
      );

      if (fnError) {
        // Extract the actual error from the edge function response.
        // supabase.functions.invoke returns { data, error, response }.
        // For non-2xx: error is FunctionsHttpError, response is the Response object.
        let detail = fnError.message || 'Edge Function error';
        console.error('[Hiring] Edge Function error:', fnError.name, fnError.message);

        // Try reading the response body from either fnResponse or fnError.context
        const resp = fnResponse || fnError.context;
        if (resp) {
          console.error('[Hiring] Response status:', resp.status, resp.statusText);
          try {
            // Clone the response so we can read the body even if it was partially consumed
            const cloned = typeof resp.clone === 'function' ? resp.clone() : null;
            const target = cloned || resp;
            if (typeof target.json === 'function') {
              const errBody = await target.json();
              console.error('[Hiring] Response body:', errBody);
              if (errBody?.error) detail = errBody.error;
              else if (errBody?.message) detail = errBody.message;
            }
          } catch {
            // JSON parse failed — try text
            try {
              const textResp = typeof resp.clone === 'function' ? resp.clone() : resp;
              if (typeof textResp.text === 'function') {
                const errText = await textResp.text();
                console.error('[Hiring] Response text:', errText);
                if (errText) {
                  try {
                    const parsed = JSON.parse(errText);
                    if (parsed?.error) detail = parsed.error;
                  } catch {
                    // Not JSON — show raw text (truncated)
                    detail = `Edge Function error (HTTP ${resp.status || '?'}): ${errText.substring(0, 200)}`;
                  }
                }
              }
            } catch { /* exhausted all strategies */ }
          }
        }
        throw new Error(detail);
      }
      if (fnData?.error) throw new Error(fnData.error);

      const newUserId = fnData?.userId;

      // ──────────────────────────────────────────────────────────────────
      // CLIENT-SIDE PROFILE POPULATION (v4)
      // The Edge Function creates the user row + archives the candidate.
      // We populate the detailed profile data client-side because the
      // Edge Function may not handle all profile sub-tables yet.
      // Each block is try/catch-ed independently so partial failures
      // don't block the overall hire flow.
      // ──────────────────────────────────────────────────────────────────
      if (newUserId) {
        // 1. Upsert user_profiles — contacts + hiring snapshot
        try {
          const latestGradDate = (candidate.degrees || [])
            .map(d => d.graduationDate).filter(Boolean).sort().reverse()[0] || null;

          await supabase.from('user_profiles').upsert({
            user_id: newUserId,
            // Contacts
            phone_mobile: candidate.phoneMobile || null,
            phone_whatsapp: candidate.phoneWhatsapp || null,
            phone_custom_label: candidate.phoneCustomLabel || null,
            phone_custom_value: candidate.phoneCustomValue || null,
            // Hiring snapshot
            home_city: candidate.locationCity || null,
            portfolio_url: candidate.portfolioUrl || null,
            past_employment: candidate.pastEmployment || [],
            years_experience: candidate.yearsExperience ?? null,
            notice_period_end_date: candidate.possibleJoiningDate || null,
            software_proficiency: latestInterview?.softwareProficiency || {},
            salary_expectations_monthly: latestInterview?.salaryExpectationsMonthly ?? null,
            current_salary_monthly: latestInterview?.currentSalaryMonthly ?? null,
            future_plans: latestInterview?.futurePlans || null,
            hiring_overall_comments: latestInterview?.overallComments || null,
            hiring_final_verdict_notes: latestInterview?.finalVerdictNotes || null,
            hiring_candidate_id: candidateId,
            // Graduation date from latest degree
            graduation_date: latestGradDate,
            // Job info (from hire form)
            current_job_role: hirePayload.jobRole || null,
            current_job_level: hirePayload.jobLevel ?? null,
            office_location: hirePayload.officeLocation || 'NL',
            joining_date: hirePayload.joinDate || null,
            // NL salary matrix placement
            nl_schaal: hirePayload.nlSchaal || null,
            nl_trede: hirePayload.nlTrede ?? null,
          }, { onConflict: 'user_id' });
        } catch (profileErr) {
          console.warn('[Hiring] Post-hire profile upsert failed (non-blocking):', profileErr);
        }

        // 2. Upsert primary home address (via useTeamMemberProfile helper)
        try {
          const addrResult = await upsertPrimaryHomeAddressForUser(newUserId, {
            addressLine1: candidate.addressLine1,
            addressLine2: candidate.addressLine2,
            addressCity: candidate.addressCity,
            addressState: candidate.addressState,
            addressPostalCode: candidate.addressPostalCode,
            addressCountry: candidate.addressCountry,
          });
          if (!addrResult.success) {
            console.warn('[Hiring] Post-hire address upsert warning:', addrResult.error);
          }
        } catch (addrErr) {
          console.warn('[Hiring] Post-hire address insert failed (non-blocking):', addrErr);
        }

        // 3. Replace employer rows from pastEmployment[] (via useTeamMemberProfile helper)
        try {
          const empResult = await replaceEmployersForUser(newUserId, candidate.pastEmployment);
          if (!empResult.success) {
            console.warn('[Hiring] Post-hire employers warning:', empResult.error);
          }
        } catch (empErr) {
          console.warn('[Hiring] Post-hire employers insert failed (non-blocking):', empErr);
        }

        // 4. Replace education rows from degrees[] (via useTeamMemberProfile helper)
        try {
          const eduResult = await replaceEducationForUser(newUserId, candidate.degrees);
          if (!eduResult.success) {
            console.warn('[Hiring] Post-hire education warning:', eduResult.error);
          }
        } catch (eduErr) {
          console.warn('[Hiring] Post-hire education insert failed (non-blocking):', eduErr);
        }
      }

      // Refetch both candidates (now archived) and interviews
      await Promise.all([fetchCandidates(), fetchInterviews()]);

      // 5. Save latest interview as a hiring note in user_notes (non-blocking)
      let noteWarning = null;
      try {
        const noteText = composeHiringInterviewNote(latestInterview, activeUsers, candidate.candidateName);
        const createdAt = latestInterview?.interviewDate
          ? new Date(latestInterview.interviewDate).toISOString()
          : new Date().toISOString();
        const noteResult = await addUserNote(
          { userId: fnData?.userId, noteType: 'Hiring Interview', note: noteText, createdAt },
          true // useSupabaseDB
        );
        if (!noteResult?.success) {
          noteWarning = noteResult?.error || 'Unknown error saving hiring note';
          console.warn('[Hiring] Note insert warning:', noteWarning);
        }
      } catch (noteErr) {
        noteWarning = noteErr.message || 'Failed to save hiring note';
        console.warn('[Hiring] Note insert failed (non-blocking):', noteErr);
      }

      return {
        success: true,
        data: {
          userId: fnData?.userId,
          latestInterview,                        // v6: for HiringPage note composition
          candidateName: candidate.candidateName,  // v6: for note header line
        },
        noteWarning,
      };
    } catch (err) {
      console.error('[Hiring] Error hiring candidate:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'hire', entity: 'candidate' }) };
    }
  };

  return {
    candidates,
    interviews,
    loading,
    listCandidates,
    createCandidate,
    updateCandidate,
    deleteCandidate,
    createInterview,
    updateInterview,
    deleteInterview,
    getInterviewsForCandidate,
    hireCandidate,
    refetch
  };
};

// ============================================================================
// LOCAL STORAGE FALLBACK HOOK
// ============================================================================
// Mirrors the Supabase hook API but uses localStorage.
// Used when useSupabaseDB === false.
// ============================================================================
export const useLocalHiring = () => {
  const LS_CANDIDATES_KEY = 'tt_hiring_candidates';
  const LS_INTERVIEWS_KEY = 'tt_hiring_interviews';

  const loadLS = (key, def) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : def;
    } catch { return def; }
  };
  const saveLS = (key, val) => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { console.error('LS save error:', e); }
  };

  const [candidates, setCandidates] = useState(() => loadLS(LS_CANDIDATES_KEY, []));
  const [interviews, setInterviews] = useState(() => loadLS(LS_INTERVIEWS_KEY, []));
  const [loading] = useState(false);

  // Persist on change
  useEffect(() => { saveLS(LS_CANDIDATES_KEY, candidates); }, [candidates]);
  useEffect(() => { saveLS(LS_INTERVIEWS_KEY, interviews); }, [interviews]);

  const listCandidates = useCallback(({ archived, category, viewMode: vm } = {}) => {
    let result = candidates;
    if (vm === 'current') {
      result = result.filter(c => !c.isArchived);
    } else if (vm === 'archived') {
      result = result.filter(c => c.isArchived && c.archiveReason !== 'Hired' && !c.hiredUserId);
    } else if (vm === 'hired') {
      result = result.filter(c => c.isArchived && (c.archiveReason === 'Hired' || !!c.hiredUserId));
    } else if (archived !== undefined) {
      result = result.filter(c => c.isArchived === archived);
    }
    if (category) result = result.filter(c => c.category === category);
    return result;
  }, [candidates]);

  const createCandidate = async (payload) => {
    const newCandidate = {
      ...payload,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isArchived: payload.isArchived ?? false,
      pastEmployment: payload.pastEmployment || [],
      archiveReason: null,
      phoneMobile: payload.phoneMobile || '',
      phoneWhatsapp: payload.phoneWhatsapp || '',
      phoneCustomLabel: payload.phoneCustomLabel || '',
      phoneCustomValue: payload.phoneCustomValue || '',
      addressLine1: payload.addressLine1 || '',
      addressLine2: payload.addressLine2 || '',
      addressCity: payload.addressCity || '',
      addressState: payload.addressState || '',
      addressPostalCode: payload.addressPostalCode || '',
      addressCountry: payload.addressCountry || '',
      degrees: payload.degrees || [],
      hiredUserId: null,
    };
    setCandidates(prev => [newCandidate, ...prev]);
    return { success: true, data: newCandidate };
  };

  const updateCandidate = async (id, updates) => {
    setCandidates(prev => prev.map(c =>
      c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
    ));
    return { success: true };
  };

  const deleteCandidate = async (id) => {
    setCandidates(prev => prev.filter(c => c.id !== id));
    setInterviews(prev => prev.filter(i => i.candidateId !== id));
    return { success: true };
  };

  const createInterview = async (candidateId, payload) => {
    const newInterview = {
      ...payload,
      id: crypto.randomUUID(),
      candidateId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setInterviews(prev => [...prev, newInterview]);
    return { success: true, data: newInterview };
  };

  const updateInterview = async (interviewId, updates) => {
    setInterviews(prev => prev.map(i =>
      i.id === interviewId ? { ...i, ...updates, updatedAt: new Date().toISOString() } : i
    ));
    return { success: true };
  };

  const deleteInterview = async (interviewId) => {
    setInterviews(prev => prev.filter(i => i.id !== interviewId));
    return { success: true };
  };

  const getInterviewsForCandidate = useCallback((candidateId) => {
    return interviews
      .filter(i => i.candidateId === candidateId)
      .sort((a, b) => a.interviewIndex - b.interviewIndex);
  }, [interviews]);

  // Local-mode hire simulation
  const hireCandidate = async (candidateId, hirePayload, activeUsers = []) => {
    const candidate = candidates.find(c => c.id === candidateId);
    if (!candidate) return { success: false, error: 'Candidate not found' };
    const fakeUserId = Date.now(); // numeric, matches users table pattern
    setCandidates(prev => prev.map(c =>
      c.id === candidateId
        ? {
            ...c,
            isArchived: true,
            archiveReason: 'Hired',
            archivedAt: new Date().toISOString(),
            hiredUserId: fakeUserId,
            hiredAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }
        : c
    ));

    // Gather latest interview for HiringPage note composition
    const candInterviews = interviews
      .filter(i => i.candidateId === candidateId)
      .sort((a, b) => b.interviewIndex - a.interviewIndex);
    const latestInterview = candInterviews[0] || null;

    // Save hiring interview note (non-blocking)
    let noteWarning = null;
    try {
      const noteText = composeHiringInterviewNote(latestInterview, activeUsers, candidate.candidateName);
      const createdAt = latestInterview?.interviewDate
        ? new Date(latestInterview.interviewDate).toISOString()
        : new Date().toISOString();
      await addUserNote(
        { userId: fakeUserId, noteType: 'Hiring Interview', note: noteText, createdAt },
        false // localStorage mode
      );
    } catch (noteErr) {
      noteWarning = noteErr.message || 'Failed to save hiring note';
      console.warn('[Hiring] Local note insert failed:', noteErr);
    }

    return {
      success: true,
      data: {
        userId: fakeUserId,
        latestInterview,                        // v6: for HiringPage note composition
        candidateName: candidate.candidateName,  // v6: for note header line
      },
      noteWarning,
    };
  };

  const refetch = async () => {
    setCandidates(loadLS(LS_CANDIDATES_KEY, []));
    setInterviews(loadLS(LS_INTERVIEWS_KEY, []));
  };

  return {
    candidates,
    interviews,
    loading,
    listCandidates,
    createCandidate,
    updateCandidate,
    deleteCandidate,
    createInterview,
    updateInterview,
    deleteInterview,
    getInterviewsForCandidate,
    hireCandidate,
    refetch
  };
};
